/* @flow */

import * as _ from "lodash";

import { SessionStore } from "shared/src/uidispatcherapi/SessionStore";
import { AnonymousCartMetaStore } from "shared/src/uidispatcherapi/AnonymousCartMetaStore";
import simplesocket from "shared/src/uidispatcherapi/SimpleSocket";
import { GdprStorage } from "shared/src/uidispatcherapi/GdprStorage";
import * as AnonymousCartClient from "../clients/AnonymousCartClient";
import { Store } from "shared/src/stores/Store";
import { generateUuid } from "shared/src/utils/util";
import { timeout } from "../utils/timeout";
import { buildPath } from "shared/src/utils/RequestUtil";
import { convertFromAnonCartItem } from "shared/src/utils/ShoppingCartPriceDataConverter";
type BettyBundleId = string;
type AnonCart = AnonymousCartClient.AnonCart;
import { ShoppingCartPosition } from "shared/src/types/ShoppingCartPosition";
import { notify } from "shared/src/utils/Notifications";
import Tracking from "shared/src/uidispatcherapi/Tracking";
import { isAnonCartEnabled } from "../searchdiscover/toggles";
import { isTier12Customer } from "shared/src/stores/SessionStore";
type CartItemChangeState = "pending" | "sending" | "sent"; // sent and 200 received, but that doesn't mean it's contained in the cart yet

type CartItemChange = {
    // we only send "set amount to x" changes, which simplifies a lot
    state: CartItemChangeState;
    requestId: string;
    bundleId: BettyBundleId;
    amount: number;
};

export class AnonymousCartStoreImpl extends Store {
    _wsConnection!: {
        close: () => void;
    } | null;
    _wsAnonCartId: string | null = null;
    _pollingCart = false;
    _sendingChanges = false;
    _anonCartId: string | null | undefined = null;
    _storeIdForAnonCartId: string | null | undefined = null;
    // full cart received from server + events from websocket we could apply ourselves
    _fullCart: AnonCart | null = null;
    // ids of all confirmed events
    _confirmedEvents: Set<string> = new Set();
    // changes sent to the server already
    _changes: Array<CartItemChange> = [];
    // predictedAmounts = _fullCart + _changes
    _predictedAmounts: {
        [x: string]: number;
    } = {};
    _currentDnrNumber: string | null = null;

    constructor() {
        super();
        this.listenTo(AnonymousCartMetaStore, this.connect);
        // make sure we connect when the SessionStore isn't pending anymore
        this.listenTo(SessionStore, this.connect);
        this.connect();
    }

    // for testing
    _reset() {
        this._wsConnection = null;
        this._wsAnonCartId = null;
        this._pollingCart = false;
        this._anonCartId = null;
        this._fullCart = null;
        this._confirmedEvents = new Set();
        this._changes = [];
        this._predictedAmounts = {};
        this._sendingChanges = false;
    }

    connect() {
        const newAnonCartId = AnonymousCartMetaStore.getCartId();
        const newStoreId = AnonymousCartMetaStore.getStoreId();
        if (newAnonCartId && newStoreId) {
            if (newAnonCartId !== this._anonCartId || newStoreId !== this._storeIdForAnonCartId) {
                this._confirmedEvents = new Set();
            }
            this._anonCartId = newAnonCartId;
            this._storeIdForAnonCartId = newStoreId;
            this._connectSocket();
        }
    }

    _connectSocket() {
        const anonCartId = this._anonCartId;
        if (this._wsAnonCartId === anonCartId || !anonCartId) {
            return;
        }
        if (!(SessionStore.notLoggedIn() || isTier12Customer())) {
            return;
        }
        if (this._wsConnection) {
            this._wsConnection.close();
        }
        this._wsAnonCartId = anonCartId;
        const url = buildPath([
            "oc",
            "anon-cart",
            "events",
            SessionStore.getCountry(),
            SessionStore.getCustomerStore(),
            SessionStore.getAnonymousUserId()
        ]);
        this._wsConnection = simplesocket.initConnection({
            channel: "/" + url,
            noAuth: true,
            onOpenFailed: () => {
                Tracking.trackError("searchdiscover", "ui", "AnonymousCartStore", "Opening websocket failed");
                this._wsConnection = null;
                this._pollAnonCart();
            },
            onClose: () => {
                this._wsConnection = null;
                this._pollAnonCart();
            },
            onMessage: (json: any) => this._handleCartEvent(json)
        });
        // it would be more correct to do this after the socket is opened, but simplesocket only calls onOpen for the
        // first subscriber to a socket
        this._reloadFullCart();
    }

    async _reloadFullCart() {
        // reload the cart, but try checking the version first
        if (!this._anonCartId) {
            return;
        }

        const cartContext = this._getCartContext();

        if (cartContext.storeId !== this._storeIdForAnonCartId) {
            //The store that belongs to the anon cart id does not match the context's store.
            //This is an inconsistent state - do not poll for anon cart version. See BT-10796.
            return;
        }
        let newVersion;
        try {
            newVersion = await AnonymousCartClient.getCartVersion({
                cartId: this._anonCartId,
                cartContext: cartContext
            });
        } catch (e: any) {
            if (e._isRequestError && e.status === 404) {
                //context does not fit to cart id any more => connect again
                this.connect();
                return;
            }
            console.warn("Cart polling error:", e);
        }

        if (
            this._anonCartId &&
            (!this._fullCart ||
                this._fullCart.cartId !== this._anonCartId ||
                (newVersion && newVersion !== this._fullCart.cartVersion))
        ) {
            let newCart;
            try {
                newCart = await AnonymousCartClient.getCart({
                    cartId: this._anonCartId,
                    cartContext: cartContext
                });

                this._handleFullCartUpdate(newCart);
            } catch (e: any) {
                Tracking.trackError("searchdiscover", "ui", "AnonymousCartStore", "Error retrieving new cart:", e);
            }
        }
    }

    async _pollAnonCart() {
        if (this._pollingCart || !this._anonCartId) {
            return;
        }
        this._pollingCart = true;
        try {
            while (true) {
                if (!this._anonCartId || this._wsConnection) {
                    return;
                }

                await this._reloadFullCart();

                await timeout(2000);
            }
        } finally {
            this._pollingCart = false;
        }
    }

    _handleFullCartUpdate(cart: AnonymousCartClient.AnonCart | undefined | null) {
        if (!cart) {
            return;
        }
        this._fullCart = cart;
        cart.eventHistory.forEach((e) => this._confirmedEvents.add(e));
        this._removeConfirmedChanges();
        this._updatePredictedAmounts();

        this.trigger();
    }

    _handleCartEvent(event: AnonymousCartClient.CartEvent) {
        // this event application logic is obviously very incomplete, it only covers the data we're interested in
        // (i.e. what's defined in the AnonCart type)
        if (
            event.status.type === "Success" &&
            (event.type === "ItemQuantitySetEvent" || event.type === "ItemAddedEvent") &&
            event.bundleId &&
            typeof event.quantity === "number" &&
            this._fullCart
        ) {
            this._fullCart.cartVersion = event.seqNum;
            this._fullCart.items[event.bundleId] = {
                bundleId: event.bundleId,
                quantity: event.quantity,
                prices: event.prices && event.prices.articlePrices[event.bundleId],
                errors: []
            };
        } else if (
            event.status.type === "Success" &&
            event.type === "ItemDeletedEvent" &&
            event.bundleId &&
            this._fullCart
        ) {
            this._fullCart.cartVersion = event.seqNum;
            delete this._fullCart.items[event.bundleId];
        } else if (event.status.type === "Success" && event.type === "CartCreatedEvent") {
            this._reloadFullCart();
        } else if (
            event.type === "ItemServiceOptionsSetEvent" ||
            event.type === "CartDeletedEvent" ||
            event.status.type === "Failure"
        ) {
            // we don't really care, OC will show an error message
        } else {
            console.log("Unknown event:", event.type, "reloading cart");
            this._reloadFullCart();
            // we don't want to immediately update our prediction in this case
            return;
        }
        this._confirmedEvents.add(event.eventId);
        this._removeConfirmedChanges();
        this._updatePredictedAmounts();

        this.trigger();
    }

    _removeConfirmedChanges() {
        _.remove(this._changes, (change) => this._confirmedEvents.has(change.requestId));
    }

    _updatePredictedAmounts() {
        const amounts = _.mapValues(
            this._fullCart === null ? {} : this._fullCart.items,
            (item: AnonymousCartClient.AnonCartItem) => item.quantity
        );
        for (const change of this._changes) {
            amounts[change.bundleId] = change.amount;
        }
        this._predictedAmounts = amounts;
    }

    _getCartContext(): AnonymousCartClient.CartContext {
        return {
            country: SessionStore.getCountry(),
            locale: SessionStore.getLocale(),
            // if these two change, we rely on the AnonymousCartMetaStore to change the anonCartId
            anonUserId: SessionStore.getAnonymousUserId(),
            storeId: SessionStore.getCustomerStore()
        };
    }

    async _sendChanges() {
        if (this._sendingChanges) {
            return;
        }
        try {
            this._sendingChanges = true;
            let errorWait = 500; // milliseconds
            while (true) {
                let change;
                const bundlesInCart = new Set(_.keys(this._fullCart ? this._fullCart.items : {}));
                // we need to keep track of what's in the cart so we know whether to do addItem or setQuantity
                for (const c of this._changes) {
                    if (c.state === "sent") {
                        bundlesInCart.add(c.bundleId);
                    } else {
                        // found one to send
                        change = c;
                        break;
                    }
                }
                if (!change) {
                    return;
                }
                change.state = "sending";

                if (!this._anonCartId) {
                    return;
                }

                const args = {
                    requestId: change.requestId,
                    cartId: this._anonCartId,
                    cartContext: this._getCartContext(),
                    bundleId: change.bundleId,
                    quantity: change.amount
                };
                try {
                    if (bundlesInCart.has(change.bundleId)) {
                        await AnonymousCartClient.setItemQuantity(args);
                    } else {
                        await AnonymousCartClient.addItem(args);
                    }
                    change.state = "sent";
                    errorWait = 500; // reset after successful request
                    await timeout(1000); // wait a bit before the next change
                } catch (e: any) {
                    if (errorWait < 10000) {
                        // FIXME(EP,FD): don't retry for 4** errors
                        console.warn("Error sending change:", e);
                        // wait a while
                        await timeout(errorWait);
                        errorWait *= 2;
                    } else {
                        // already tried too often
                        Tracking.trackError("searchdiscover", "ui", "AnonymousCartStore", "Error sending change:", e);
                        const notification = {
                            type: "warning" as const,
                            key: "EX_ADD_TO_BASKET_FAILED",
                            values: [],
                            id: "EX_ADD_TO_BASKET_FAILED"
                        };
                        notify(notification);
                        _.pull(this._changes, change);
                        this._updatePredictedAmounts();
                        this.trigger();
                        errorWait = 500;
                    }
                }
            }
        } finally {
            this._sendingChanges = false;
        }
    }

    _generateRequestId(): string {
        return "BTEX-" + generateUuid();
    }

    handleSetItemInCart(bundleId: BettyBundleId, amount: number) {
        const anonCartId = this._anonCartId;
        if (!anonCartId) {
            return;
        }
        const requestId = this._generateRequestId();
        const change = {
            state: "pending" as const,
            requestId,
            bundleId,
            amount
        };
        // remove preceding pending changes to the same item
        _.remove(this._changes, (change) => change.bundleId === bundleId && change.state === "pending");
        this._changes.push(change);
        this._updatePredictedAmounts();
        this._sendChanges();

        this.trigger();
    }

    handleChangeAmountInCart(bettyBundleId: BettyBundleId, byAmount: number) {
        const amount = _.round(byAmount + this._predictedAmounts[bettyBundleId], 2);
        this.handleSetItemInCart(bettyBundleId, amount);
    }

    handleBulkAddToBasket(): void {
        // nothing to do for anonymous carts.
    }

    getItemCount(): number {
        return _.size(this._predictedAmounts);
    }

    getItemAmount(bettyBundleId: BettyBundleId): number | null {
        return bettyBundleId in this._predictedAmounts ? this._predictedAmounts[bettyBundleId] : null;
    }

    getCartItemTotalNetPrice(bettyBundleId: BettyBundleId): ShoppingCartPosition | null {
        const cart = this._fullCart;
        if (!cart) {
            return null;
        }
        const item = cart.items[bettyBundleId];
        if (!item) {
            return {
                loading: bettyBundleId in this._predictedAmounts,
                prices: null
            };
        } else {
            return {
                loading: item.quantity !== this._predictedAmounts[bettyBundleId],
                prices: convertFromAnonCartItem(item.prices)
            };
        }
    }

    isEmpty(): boolean {
        return _.isEmpty(this._predictedAmounts);
    }

    isEnabled(): boolean {
        return isAnonCartEnabled() || GdprStorage.localStorage.read("anonCart") === "true";
    }

    getAllCartItems(): any[] {
        if (this._fullCart) {
            return Object.values(this._fullCart.items);
        }
        return [];
    }

    getCartCurrentDnrNumbers(): string[] {
        return [];
    }

    getPricesWithDnrInCart(): null {
        return null;
    }
}

export const AnonymousCartStore = new AnonymousCartStoreImpl();
